"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  setAddonQuantity as setQuantity,
  toggleAddonId,
} from "@/components/visual-builder/service-addon-quantities";

import { getSelfSchedulePriceRange } from "@/lib/service-addon-utils";
import { computeAddonMinutes } from "@/lib/service-utils";

import type {
  PublicServiceAddon,
  PublicServiceDetail,
  ServiceTiny,
} from "@/types/service";

interface UseSelectionsDrawerParams {
  service: PublicServiceDetail;
  initialAddonIds?: string[];
}

interface UseSelectionsDrawerReturn {
  /** Picked add-on ids, with repeats for an add-on picked more than once. */
  draftAddonIds: string[];
  toggleAddon: (addonId: string) => void;
  setAddonQuantity: (addonId: string, quantity: number) => void;
  isValid: boolean;
  validationMessage: string;
  totalPriceMin: number;
  totalPriceMax: number;
  totalDuration: number;
  selectedAddons: PublicServiceAddon[];
  reset: () => void;
}

function getValidationMessage(
  selectionMin: number,
  selectionMax: number | null,
): string {
  if (selectionMin === 0 && selectionMax === null) return "Optional";
  if (selectionMin === 1 && selectionMax === 1) return "Pick 1";
  if (selectionMin === selectionMax && selectionMin > 0)
    return `Pick ${selectionMin}`;
  if (selectionMin > 0 && selectionMax === null)
    return `Pick at least ${selectionMin}`;
  if (selectionMin === 0 && selectionMax !== null)
    return `Pick up to ${selectionMax}`;
  if (selectionMin > 0 && selectionMax !== null)
    return `Pick ${selectionMin} to ${selectionMax}`;
  return "Optional";
}

export function useSelectionsDrawer({
  service,
  initialAddonIds,
}: UseSelectionsDrawerParams): UseSelectionsDrawerReturn {
  const addons = service.addons ?? [];
  const selectionMin = service.selectionMin ?? 0;
  const selectionMax = service.selectionMax ?? null;

  const defaultAddonIds = useMemo(
    () => initialAddonIds ?? addons.filter((a) => a.isDefault).map((a) => a.id),
    [initialAddonIds, addons],
  );

  const [draftAddonIds, setDraftAddonIds] = useState<string[]>(defaultAddonIds);

  useEffect(() => {
    setDraftAddonIds(defaultAddonIds);
  }, [defaultAddonIds]);

  const toggleAddon = useCallback(
    (addonId: string) => {
      setDraftAddonIds((prev) => toggleAddonId(service, prev, addonId));
    },
    [service],
  );

  const setAddonQuantity = useCallback(
    (addonId: string, quantity: number) => {
      setDraftAddonIds((prev) => setQuantity(service, prev, addonId, quantity));
    },
    [service],
  );

  // Keep existing drafts visible so patients can correct them rather than
  // silently dropping paid selections when a service's setting changes.
  const hasDisallowedRepeats = useMemo(
    () =>
      addons.some(
        (addon) =>
          addon.addonService.allowMultipleBookings !== true &&
          draftAddonIds.filter((id) => id === addon.id).length > 1,
      ),
    [addons, draftAddonIds],
  );

  const isValid = useMemo(() => {
    if (hasDisallowedRepeats) return false;
    if (selectionMin > 0 && draftAddonIds.length < selectionMin) return false;
    if (selectionMax !== null && draftAddonIds.length > selectionMax)
      return false;
    return true;
  }, [draftAddonIds.length, selectionMin, selectionMax, hasDisallowedRepeats]);

  const validationMessage = useMemo(
    () =>
      hasDisallowedRepeats
        ? "Remove repeated add-ons that no longer allow multiple bookings"
        : getValidationMessage(selectionMin, selectionMax),
    [selectionMin, selectionMax, hasDisallowedRepeats],
  );

  const selectedAddons = useMemo(
    () =>
      draftAddonIds
        .map((id) => addons.find((addon) => addon.id === id))
        .filter((addon): addon is PublicServiceAddon => addon !== undefined),
    [addons, draftAddonIds],
  );

  const totalPriceMin = useMemo(
    () =>
      selectedAddons.reduce(
        (sum, addon) =>
          sum +
          getSelfSchedulePriceRange(addon.addonService as ServiceTiny).min,
        0,
      ),
    [selectedAddons],
  );

  const totalPriceMax = useMemo(
    () =>
      selectedAddons.reduce((sum, addon) => {
        const { min, max } = getSelfSchedulePriceRange(
          addon.addonService as ServiceTiny,
        );
        return sum + Math.max(min, max);
      }, 0),
    [selectedAddons],
  );

  const totalDuration = useMemo(
    () =>
      computeAddonMinutes(
        service,
        selectedAddons.map((addon) => addon.addonService.duration),
      ),
    [service, selectedAddons],
  );

  const reset = useCallback(() => {
    setDraftAddonIds(defaultAddonIds);
  }, [defaultAddonIds]);

  return {
    draftAddonIds,
    toggleAddon,
    setAddonQuantity,
    isValid,
    validationMessage,
    totalPriceMin,
    totalPriceMax,
    totalDuration,
    selectedAddons,
    reset,
  };
}
